import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sparkles, Check } from "lucide-react";
import PublicLayout, { Eyebrow, PrimaryBtn, GhostBtn, H2 } from "@/components/public/PublicLayout";
import { ROUTES, EXTERNAL, C, SERIF } from "@/lib/publicLinks";
import { useOfferLinks } from "@/lib/useOfferLinks";

// ---- Deterministic routing data (no AI calls needed for simple routing) ----
const CONCERNS = [
  { key: "hair", label: "Hair shedding or thinning", hair: true },
  { key: "scalp", label: "Scalp concerns (flaking, itch, buildup)", hair: true },
  { key: "energy", label: "Fatigue / low energy" },
  { key: "sleep", label: "Poor sleep" },
  { key: "stress", label: "Stress / feeling overwhelmed", emotional: true },
  { key: "digestion", label: "Digestion changes" },
  { key: "weight", label: "Weight or metabolic concerns" },
  { key: "womens", label: "Women's wellness changes", emotional: true },
  { key: "general", label: "General wellness / not sure" },
];
const DURATION = [
  { key: "short", label: "Less than 3 months", score: 0 },
  { key: "mid", label: "3–12 months", score: 1 },
  { key: "long", label: "More than 12 months", score: 2 },
];
const SYSTEMS = [
  { key: "one", label: "Just one thing", score: 0 },
  { key: "few", label: "A few things at once", score: 1 },
  { key: "many", label: "Many things at once", score: 2 },
];
const PREFERENCE = [
  { key: "virtual", label: "Virtual (from home)" },
  { key: "inperson", label: "In-person in Raleigh, NC" },
  { key: "either", label: "No preference" },
];

const REC = {
  intro: { title: "KISSED Wellness Assessment™ — Intro", price: "$47", href: EXTERNAL.squareTierIntro, kind: "virtual" },
  standard: { title: "KISSED Wellness Assessment™ — Standard", price: "$147", href: EXTERNAL.squareTierStandard, kind: "virtual" },
  premium: { title: "KISSED Wellness Assessment™ — Premium", price: "$297", href: EXTERNAL.squareTierPremium, kind: "virtual" },
  micro: { title: "Microscopic Hair & Scalp Analysis", price: "In-clinic visit · $250 (with ZYTO scan $350)", href: EXTERNAL.bookHairScalpAnalysis, kind: "inperson" },
  investigation: { title: "KISSED Hair Loss Investigation™", price: "In-clinic visit · $499", href: EXTERNAL.bookHairInvestigation, kind: "inperson" },
};

function recommend(a) {
  const concern = CONCERNS.find((c) => c.key === a.concern) || {};
  const dur = DURATION.find((d) => d.key === a.duration)?.score ?? 0;
  const sys = SYSTEMS.find((s) => s.key === a.systems)?.score ?? 0;
  const complexity = dur + sys; // 0..4
  const tierKey = complexity >= 3 ? "premium" : complexity === 2 ? "standard" : "intro";

  let rec, why;
  if (concern.hair && a.preference === "inperson") {
    if (concern.key === "scalp" && complexity < 3) {
      rec = REC.micro;
      why = "A focused scalp concern is often best seen directly. A magnified, in-person look lets your practitioner assess what's happening on the surface and recommend next steps.";
    } else {
      rec = REC.investigation;
      why = "Persistent or complex hair loss deserves a broader, in-person investigation — history, microscopic analysis, and wellness screening together, rather than one symptom at a time.";
    }
  } else {
    rec = REC[tierKey];
    if (concern.hair) {
      why = "Your hair concerns can begin virtually. Based on how long this has been going on and how many things are changing at once, this assessment depth fits where you are today — with in-person options available if your findings call for it.";
    } else {
      why = `Because ${complexity >= 2 ? "several things are changing at once" : "this is early and focused"}, this assessment depth looks at the right breadth of your wellness patterns — energy, sleep, stress, digestion, and more — to find your most useful next step.`;
    }
  }

  const showCheckin = concern.emotional || dur === 2;
  return { rec, why, showCheckin, tierKey };
}

function Choice({ options, value, onChange, name }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {options.map((o) => {
        const active = value === o.key;
        return (
          <button key={o.key} type="button" onClick={() => onChange(o.key)}
            data-testid={`nav-${name}-${o.key}`}
            className="text-left rounded-xl px-4 py-3 text-sm transition flex items-center justify-between gap-2"
            style={{ border: `1px solid ${active ? C.primary : C.line}`, background: active ? "#F3E8FF" : "#fff", color: C.ink }}>
            <span>{o.label}</span>
            {active && <Check className="h-4 w-4 flex-shrink-0" style={{ color: C.primary }} />}
          </button>
        );
      })}
    </div>
  );
}

export default function WellnessNavigator() {
  const [step, setStep] = useState(0);
  const [a, setA] = useState({ concern: "", duration: "", systems: "", preference: "" });
  const set = (k) => (v) => setA((s) => ({ ...s, [k]: v }));

  const steps = [
    { title: "What sounds most like you right now?", sub: "Choose your primary concern.", el: <Choice options={CONCERNS} value={a.concern} onChange={set("concern")} name="concern" />, valid: !!a.concern },
    { title: "How long has this been going on?", el: <Choice options={DURATION} value={a.duration} onChange={set("duration")} name="duration" />, valid: !!a.duration },
    { title: "How much feels affected?", sub: "This helps us match the right depth.", el: <Choice options={SYSTEMS} value={a.systems} onChange={set("systems")} name="systems" />, valid: !!a.systems },
    { title: "How would you prefer to begin?", el: <Choice options={PREFERENCE} value={a.preference} onChange={set("preference")} name="preference" />, valid: !!a.preference },
  ];
  const isResult = step >= steps.length;
  const links = useOfferLinks({ investigation: EXTERNAL.bookHairInvestigation, micro: EXTERNAL.bookHairScalpAnalysis });
  const result = isResult ? recommend(a) : null;
  const recHref = result && (result.rec === REC.investigation ? links.investigation : result.rec === REC.micro ? links.micro : result.rec.href);

  return (
    <PublicLayout>
      <section style={{ background: C.lightBg }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <Eyebrow>KISSED Initial Wellness Navigator</Eyebrow>
          {!isResult ? (
            <>
              <div className="flex items-center gap-2 mb-6">
                {steps.map((_, i) => (
                  <span key={i} className="h-1.5 rounded-full flex-1" style={{ background: i <= step ? C.primary : C.line }} />
                ))}
              </div>
              <h1 className="text-2xl sm:text-3xl text-slate-900" style={SERIF} data-testid="navigator-q-title">{steps[step].title}</h1>
              {steps[step].sub && <p className="mt-2 text-sm text-slate-600">{steps[step].sub}</p>}
              <div className="mt-6">{steps[step].el}</div>
              <div className="mt-8 flex items-center justify-between">
                <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))}
                  className="inline-flex items-center gap-1 text-sm text-slate-500 disabled:opacity-40" disabled={step === 0} data-testid="navigator-back">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button type="button" onClick={() => steps[step].valid && setStep((s) => s + 1)}
                  disabled={!steps[step].valid}
                  className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition disabled:opacity-40"
                  style={{ background: C.primary }} data-testid="navigator-next">
                  {step === steps.length - 1 ? "See My Recommendation" : "Continue"} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </>
          ) : (
            <div data-testid="navigator-result">
              <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: C.accent }}>
                <Sparkles className="h-4 w-4" /> Your Recommended Starting Point
              </div>
              <h1 className="mt-3 text-3xl sm:text-4xl text-slate-900" style={SERIF} data-testid="navigator-rec-title">{result.rec.title}</h1>
              <div className="mt-2 text-slate-700 font-medium">{result.rec.kind === "virtual" ? `Virtual assessment · ${result.rec.price}` : result.rec.price}</div>

              <div className="mt-8 rounded-2xl bg-white p-6" style={{ border: `1px solid ${C.line}` }}>
                <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.accent }}>Why This Is Recommended</div>
                <p className="mt-2 text-slate-700">{result.why}</p>
              </div>

              <div className="mt-4 rounded-2xl bg-white p-6" style={{ border: `1px solid ${C.line}` }}>
                <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.accent }}>What Happens Next</div>
                {result.rec.kind === "virtual" ? (
                  <p className="mt-2 text-slate-700">Complete your secure checkout, then you'll return to KISSED to begin your assessment right away. Your practitioner reviews your findings and prepares your personalized next steps.</p>
                ) : (
                  <p className="mt-2 text-slate-700">Book your in-person visit in Raleigh, NC. Your practitioner will assess in person and recommend personalized next steps.</p>
                )}
              </div>

              <div className="mt-8">
                <PrimaryBtn href={recHref} className="w-full sm:w-auto" data-testid="navigator-continue">
                  {result.rec.kind === "virtual" ? "Purchase This Assessment" : "Book This Assessment"} <ArrowRight className="h-4 w-4" />
                </PrimaryBtn>
                <p className="mt-3 text-xs text-slate-500">This free recommendation is a starting point only — it is not a clinical intake and does not authorize any treatment.</p>
              </div>

              <div className="mt-6 text-sm text-slate-700" data-testid="navigator-other-options">
                <span className="font-semibold">Prefer something else? </span>
                <a href="/wellness#choose-depth" className="hover:underline" style={{ color: C.accent }}>Virtual assessments</a>
                <span className="text-slate-400"> · </span>
                <Link to={ROUTES.hairScalp} className="hover:underline" style={{ color: C.accent }}>Hair &amp; scalp assessments</Link>
                <span className="text-slate-400"> · </span>
                <Link to="/in-clinic-services" className="hover:underline" style={{ color: C.accent }}>In-Clinic Services</Link>
              </div>

              {result.showCheckin && (
                <div className="mt-6 rounded-2xl p-6" style={{ background: C.lightBg }} data-testid="navigator-checkin">
                  <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.accent }}>Additional Support — Check In With Dr. C</div>
                  <p className="mt-2 text-sm text-slate-700">Wellness journeys can carry stress, adjustment, and emotional weight. Check In With Dr. C offers optional, complementary supportive reflection — it doesn't replace your recommendation above, and it isn't therapy or mental-health treatment.</p>
                  <div className="mt-4"><GhostBtn to={ROUTES.checkin}>Learn about Check In With Dr. C</GhostBtn></div>
                </div>
              )}

              <div className="mt-8 text-sm">
                <button type="button" onClick={() => setStep(0)} className="hover:underline" style={{ color: C.accent }} data-testid="navigator-restart">Start over</button>
                <span className="text-slate-400"> · </span>
                <Link to={ROUTES.wellness} className="hover:underline" style={{ color: C.accent }}>See all assessment options</Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
